import React, { useState, useEffect } from "react";
import classes from "./SavingsMod.module.css";
import api, { BASE_URL, withCompanyId } from "../../../Components/API/Api";
import { Button, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import crop from "../../../Assets/repoort.png";
import verified from "../../../Assets/verified.svg";
import uploadIcon from "../../../Assets/uppload.svg";
import checkersicon from "../../../Assets/checkers.svg";
import closebtn from "../../../Assets/close-btn.svg";

function SavingsMod({ isOpen, onClose, onSuccess }) {
  // --- State ---
  const [selectedLoanProducts, setSelectedLoanProducts] = useState("");
  const [loanProducts, setLoanProducts] = useState([]);
  const [loanProductsLoading, setLoanProductsLoading] = useState(false);

  const [debitAccount, setDebitAccount] = useState("");
  const [debitSearch, setDebitSearch] = useState("");
  const [showDebitDropdown, setShowDebitDropdown] = useState(false);
  const [creditAccounts, setCreditAccounts] = useState([]);
  const [debitAccountsLoading, setDebitAccountsLoading] = useState(false);

  const [supportingDocument, setSupportingDocument] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // --- Reset all form fields ---
  const resetFormFields = () => {
    setSelectedLoanProducts("");
    setDebitAccount("");
    setDebitSearch("");
    setShowDebitDropdown(false);
    setSupportingDocument(null);
  };

  // --- Fetch Savings Products ---
  useEffect(() => {
    const fetchLoanProducts = async () => {
      setLoanProductsLoading(true);
      try {
        const res = await api.get("/v1/savings_product");
        if (res.data && Array.isArray(res.data.data)) {
          setLoanProducts(res.data.data);
        } else {
          setLoanProducts([]);
        }
      } catch (err) {
        console.error("Error fetching savings products:", err);
        setLoanProducts([]);
      } finally {
        setLoanProductsLoading(false);
      }
    };
    if (isOpen) fetchLoanProducts();
  }, [isOpen]);

  // --- Fetch Bank Accounts ---
  useEffect(() => {
    const fetchCreditAccounts = async () => {
      setDebitAccountsLoading(true);
      try {
        const res = await api.get("/v1/bank-accounts");
        if (res.data && Array.isArray(res.data.data)) {
          const accounts = res.data.data.map((acc) => ({
            value: acc.id,
            label: acc.gl_name || acc.name || acc.account_name,
            gl_code: acc.gl_code || acc.code,
            fullAccount: acc,
          }));
          setCreditAccounts(accounts);
        } else {
          setCreditAccounts([]);
        }
      } catch (error) {
        console.error("Error fetching bank accounts:", error);
        setCreditAccounts([]);
      } finally {
        setDebitAccountsLoading(false);
      }
    };
    if (isOpen) fetchCreditAccounts();
  }, [isOpen]);

  // --- Handle File Selection ---
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setSupportingDocument(null);
      return;
    }

    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
      "application/vnd.ms-excel", // xls
      "text/csv", // csv
    ];

    const extension = file.name.split(".").pop()?.toLowerCase();

    if (
      !allowedTypes.includes(file.type) &&
      !["xlsx", "xls", "csv"].includes(extension)
    ) {
      Swal.fire({
        icon: "error",
        title: "Invalid File",
        text: "Only Excel (.xlsx, .xls) or CSV files are allowed.",
      });
      e.target.value = "";
      return;
    }

    setSupportingDocument(file);
  };

  // --- Submit Handler ---
  const handleSubmit = async () => {
    setIsSaving(true);

    try {
      const companyId =
        localStorage.getItem("company_id") || localStorage.getItem("companyId");

      let bankId = "";
      try {
        const bankData = JSON.parse(debitAccount);
        bankId = bankData.id;
      } catch {
        bankId = debitAccount;
      }

      const formData = new FormData();
      formData.append("company_id", companyId);
      formData.append("savings_id", selectedLoanProducts);
      formData.append("bank_id", bankId);
      if (supportingDocument) {
        formData.append("file", supportingDocument);
      }

      const response = await api.post(
        "/v1/member/import-savings-data-migration",
        withCompanyId(formData),
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          imageUrl: verified,
          title: "Success",
          text: response.data.message,
          confirmButtonColor: "#16A34A",
        }).then(() => {
          resetFormFields();
          if (onSuccess) {
            onSuccess();
          }
        });
      }
      onClose();
    } catch (error) {
      console.error(error);
      Swal.fire({
        imageUrl: crop,
        title: "Submission Failed",
        text:
          typeof error.response?.data?.message === "object"
            ? Object.values(error.response.data.message).flat().join(", ")
            : error.response?.data?.message ||
              "An error occurred while submitting the application.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Download Template ---
  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("authToken");
      const companyId =
        localStorage.getItem("company_id") || localStorage.getItem("companyId");

      const res = await api.get(
        `/v1/download-member-upload-template?company_id=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/octet-stream",
          },
          responseType: "blob",
        }
      );

      if (res.data.type === "application/json") {
        const text = await res.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.message || "Server returned an error.");
      }

      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      const disposition = res.headers["content-disposition"];
      let filename = "savings_data_migration.xlsx";
      if (disposition) {
        const match = disposition.match(
          /filename\*=UTF-8''(.+)|filename="?([^";]+)"?/
        );
        if (match) filename = decodeURIComponent(match[1] || match[2]);
      }

      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: error.message || "Unable to download template.",
      });
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={classes.modalOverlay}>
      <div className={classes.modalContent}>
        <h3 className={classes.title}>Savings Data Migration</h3>
        <h3 className={classes.titlesd}>Three steps to migrate your savings data</h3>

        {/* Status Row */}
        <div className={classes.instRow}>
          <img src={checkersicon} alt="checkers" />
          <p className={classes.instTxt}>
            Template downloaded - now upload your completed file below
          </p>
        </div>

        {/* File Upload (Sits above the fields, full width) */}
        <div
          className={classes.uploadContainer}
          onClick={() => document.getElementById("excelUpload").click()}
        >
          <div className={classes.uploadIcon}>
            <img src={uploadIcon} alt="Upload" style={{ width: 50, objectFit: "contain", paddingLeft: 10 }} />
          </div>
          <div>
            <h4>
              {supportingDocument
                ? supportingDocument.name
                : "Click to upload your completed file"}
            </h4>
            <p>or drag and drop .xlsx, .xls accepted</p>
          </div>
        </div>
        <input
          id="excelUpload"
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {/* Vertical Form Fields (Stacked) */}
        <div className={classes.topRow}>
          {/* Savings Product */}
          <div className={classes.Group3}>
            <label>Savings Product</label>
            <select
              value={selectedLoanProducts}
              onChange={(e) => setSelectedLoanProducts(e.target.value)}
            >
              <option value="">Select Product</option>
              {loanProductsLoading ? (
                <option>Loading products...</option>
              ) : (
                loanProducts.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.product_name || type.type_name}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Bank Account */}
          <div className={classes.Group2}>
            <label>Bank Account</label>
            <div className={classes.searchableDropdown}>
              <input
                type="text"
                placeholder="Search or select bank account..."
                value={debitSearch}
                onChange={(e) => setDebitSearch(e.target.value)}
                onFocus={() => setShowDebitDropdown(true)}
                onClick={() => setShowDebitDropdown(!showDebitDropdown)}
                className={classes.accountInput}
                disabled={debitAccountsLoading}
              />

              {showDebitDropdown && (
                <div
                  className={classes.dropdownList}
                  onMouseLeave={() => setShowDebitDropdown(false)}
                >
                  {debitAccountsLoading ? (
                    <div>Loading accounts...</div>
                  ) : creditAccounts.length === 0 ? (
                    <div>No accounts found</div>
                  ) : (
                    creditAccounts
                      .filter((acc) =>
                        (acc.label || "")
                          .toLowerCase()
                          .includes(debitSearch.toLowerCase())
                      )
                      .map((acc) => (
                        <div
                          key={acc.value}
                          className={classes.dropdownItem}
                          onClick={() => {
                            setDebitAccount(
                              JSON.stringify({ id: acc.value, gl_code: acc.gl_code })
                            );
                            setDebitSearch(acc.label || "");
                            setShowDebitDropdown(false);
                          }}
                        >
                          {acc.label}
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={classes.footerButtons}>
          <button type="button" className={classes.resetBtn} onClick={resetFormFields}>
            Cancel
          </button>

          <Button
            variant="success"
            className={classes.submitBtn}
            disabled={isSaving}
            onClick={handleSubmit}
          >
            {isSaving ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Spinner animation="border" size="sm" /> Saving...
              </span>
            ) : (
              "Save"
            )}
          </Button>
        </div>

        <button className={classes.closeBtn} onClick={onClose}>
          <img src={closebtn} alt="close button" />
        </button>
      </div>
    </div>
  );
}

export default SavingsMod;